import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import { ComponentDesign, ComponentType, FeaturePatternTrussSettings } from '@paragon/contract-library';
import { DistanceField, NumericField } from '@paragon/material';

import { updateActiveFeaturePatternTrussSettings } from '../../../../../../store/actions/editorActions';
import { setComponentTypeForFeaturePatternTrussInsertion } from '../../../../../../store/actions/insertComponentDesignActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { LabeledBox } from '../../../properties/LabeledBox';
import SettingsButtons from '../../../properties/SettingsButtons';
import { InsertionComponentZoneSettingsPanel, ZoneSettingsPanel } from '../../../properties/ZoneSettings';

interface UncontrolledModeProps {
  forInsertionComponent: true;
}

interface ControlledModeProps {
  forInsertionComponent?: false;

  activeComponentDesign?: ComponentDesign;

  value: FeaturePatternTrussSettings;
  onChange(newValue: FeaturePatternTrussSettings): void;
}

export function FeaturePatternTrussStructuralDetails(props: UncontrolledModeProps | ControlledModeProps) {
  const onChange =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (value: FeaturePatternTrussSettings[K]) => {
      if (props.forInsertionComponent)
        throw new Error('FeaturePatternTrussSettings onChange called in uncontrolled mode.');

      props.onChange({ ...props.value, [key]: value });
    };

  return (
    <>
      {props.forInsertionComponent ? (
        <InsertionGeneralInfo />
      ) : (
        <ControlledGeneralInfo value={props.value} onChange={props.onChange} />
      )}

      <LabeledBox title="Zone Settings">
        {props.forInsertionComponent ? (
          <InsertionComponentZoneSettingsPanel />
        ) : (
          <ZoneSettingsPanel
            values={props.value.zoneSettings}
            onChange={(updatedZone) => {
              onChange('zoneSettings')(
                props.value.zoneSettings.map((zone) => (zone.guid === updatedZone.guid ? updatedZone : zone)),
              );
            }}
          />
        )}
      </LabeledBox>

      {props.forInsertionComponent ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', mt: 2 }}>
          <SettingsButtons />
        </Box>
      ) : null}
    </>
  );
}

const textFieldSx = { m: 1, flex: 1 } as const;

function InsertionGeneralInfo() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component');

  const settings = insertingObjectInfo.designSettings;
  if (settings.definitionType !== 'FeaturePattern') throw new Error('Expected to be inserting a profile truss');

  const plies = settings.plies;
  const componentThickness = settings.thickness;
  const spacingMinimum = plies * componentThickness;

  const onChangeFeaturePatternSetting =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (value: FeaturePatternTrussSettings[K]) => {
      if (settings[key] !== value) {
        dispatch(updateActiveFeaturePatternTrussSettings({ ...settings, [key]: value }));
      }
    };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <FormControl variant="outlined" sx={textFieldSx} size="small">
        <InputLabel id="component-type-label">Component Type (Loading)</InputLabel>
        <Select
          labelId="component-type-label"
          id="component-type-select"
          value={settings.componentType}
          label="Component Type (Loading)"
          onChange={async (event) => {
            await dispatch(setComponentTypeForFeaturePatternTrussInsertion(event.target.value as ComponentType));
          }}
        >
          <MenuItem value="RoofTruss">Roof</MenuItem>
          <MenuItem value="FloorTruss">Floor</MenuItem>
        </Select>
      </FormControl>
      <DistanceField
        label="Spacing"
        value={settings.spacing}
        onSubmit={onChangeFeaturePatternSetting('spacing')}
        sx={textFieldSx}
        min={spacingMinimum}
      />
      <NumericField
        label="Plies"
        sx={textFieldSx}
        value={plies}
        min={1}
        onSubmit={onChangeFeaturePatternSetting('plies')}
      />
    </Box>
  );
}

interface ControlledGeneralInfoProps {
  value: FeaturePatternTrussSettings;
  onChange: (newValue: FeaturePatternTrussSettings) => void;
}

function ControlledGeneralInfo({ value, onChange }: ControlledGeneralInfoProps) {
  const plies = value.plies;
  const componentThickness = value.thickness;
  const spacingMinimum = plies * componentThickness;

  const onChangeProperty =
    <K extends keyof FeaturePatternTrussSettings>(key: K) =>
    (propertyValue: FeaturePatternTrussSettings[K]) => {
      onChange({ ...value, [key]: propertyValue });
    };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <DistanceField
        label="Spacing"
        value={value.spacing}
        onSubmit={onChangeProperty('spacing')}
        sx={textFieldSx}
        min={spacingMinimum}
      />
      <NumericField label="Plies" sx={textFieldSx} value={plies} min={1} onSubmit={onChangeProperty('plies')} />
    </Box>
  );
}
